import { createFileRoute, Link, useBlocker } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  Trash2,
  Save,
  AlertCircle,
  Check,
  X,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Combobox } from "@/components/ui/combobox";
import { ScrollableTable } from "@/components/ui/scrollable-table";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  myappApi,
  isRecordChangedFailure,
  VERSION_TOKEN_KEY,
  type RecordChangedDetail,
} from "@/services/api/myapp";
import { showBatchConflictToast } from "@/components/portal/tables/RecordConflictToast";
import { useAuth } from "@/contexts/AuthContext";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import type {
  CurrentStateInfo,
  FormDetail,
  FormField,
  LinkedOption,
  RecordStateContext,
  TableState,
} from "@/types/myapp";
import { extractRecordState } from "@/lib/myapp/extract-record-state";
import {
  decimalCellStep,
  DEFAULT_DECIMAL_PLACES,
  exceedsDecimalPrecision,
  exceedsMoneyPrecision,
} from "@/lib/myapp/numeric-input-step";
import {
  countMovable,
  planBulkStateMove,
  runBulkStateMove,
  type BulkMovePlan,
  type BulkMoveRow,
  type NotMovedRow,
  type RefusedRow,
} from "@/lib/myapp/bulk-state-move";
import {
  resolvePreAdvanceReview,
  type PreAdvanceReviewProps,
} from "@/components/portal/tables/state-machine/preAdvanceReview";
import { evaluateFormula, evaluateConcatenation, buildFieldValueMap } from "@/lib/myapp/formula-evaluator";
import { evaluateIr, isComputedOnSave } from "@/lib/myapp/tsql-ir";
import {
  buildBatchUpdatePayload,
  buildStateLockdownContext,
  isFieldLockedByState,
  lockReasonForField,
} from "@/lib/myapp/state-lockdown";
import {
  getSharedFkCollisionColumns,
  isSharedFkCollisionField,
} from "@/lib/myapp/shared-fk-collision";
import { SharedFkCollisionNotice } from "@/components/portal/tables/SharedFkCollisionNotice";
import { OptionsFieldInput } from "@/components/portal/tables/OptionsFieldInput";
import { parseOptionsConfig } from "@/lib/myapp/options";

// Lookup values stored per row: rowId -> fieldId -> value
type RowLookupValues = Record<string, Record<string, unknown>>;
import { formatNumber } from "@/lib/myapp/format-number";
import { formatCellValue } from "@/lib/myapp/format-cell-value";
import { toDisplayPercentage, toStoredPercentage } from "@/lib/myapp/percentage-utils";
import { canAddRecords, manualEntryBlockedReason } from "@/lib/myapp/tableCapabilities";

// System columns that hold workflow state. Never editable via Grid Edit —
// state changes go through the dedicated transition endpoint with its own
// role-gating (AdvanceRole / ReverseRole per state). See data_service
// `_check_state_lockdown` which rejects direct writes server-side anyway.
const STATE_SYSTEM_COLUMNS = new Set(["SysCurrentStateId", "SysCurrentStateName"]);

// Dirty-detection equality between the ORIGINAL value (as the record arrived)
// and the value a cell just COMMITTED. Numeric cells commit numbers while the
// record may have carried the same value as a numeric string ("249.0000"), so
// when the committed side is a number compare numerically. When the committed
// side is anything else — a text cell's string, a boolean, null — use strict
// equality: "007" → "7" in a text column IS an edit and must stay dirty.
function cellValuesEqual(original: unknown, committed: unknown): boolean {
  if (original === committed) return true;
  if (typeof committed !== "number" || isNaN(committed)) return false;
  const numericOriginal =
    (typeof original === "number" && !isNaN(original)) ||
    (typeof original === "string" && original.trim() !== "" && !isNaN(Number(original)));
  return numericOriginal && Number(original) === committed;
}

// Sticky cells need an opaque background or scrolled columns show through.
// Mirrors the per-status tint the row itself carries so the pinned cells
// don't stand out as a different colour from the rest of a saved/failed row.
function rowStickyBg(row: GridRow): string {
  if (row._status === "success") return "bg-green-50";
  if (row._status === "error") return "bg-red-50";
  return "bg-background";
}

// Seed a money/decimal <Input type="number"> with the value at the field's
// configured decimal places (default 2) so a stored 249.0000 reads "249.00".
// Presentation only — the row value stays numeric and dirty detection
// compares numbers, so seeding never marks a row dirty.
function displayNumber(value: unknown, field: FormField): string {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) return String(value);
  return num.toFixed(field.decimal_places ?? 2);
}

interface GridRow {
  _rowId: string; // Temporary ID for tracking
  _status: "pending" | "saving" | "success" | "error";
  _error?: string;
  _pkValue?: number; // Primary key value for edit mode
  [key: string]: unknown;
}

// Resizable column header component
function ResizableHeader({
  fieldId,
  width,
  onResize,
  children,
}: {
  fieldId: string;
  width: number;
  onResize: (id: string, width: number) => void;
  children: React.ReactNode;
}) {
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX.current;
      const newWidth = Math.max(80, startWidth.current + delta);
      onResize(fieldId, newWidth);
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, fieldId, onResize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
  };

  return (
    <TableHead
      style={{ width: `${width}px`, minWidth: `${width}px` }}
      className="relative"
    >
      {children}
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50"
        onMouseDown={handleMouseDown}
      />
    </TableHead>
  );
}

// How each numeric field-type code maps between the row's STORED value and the
// text in its <input type="number">. `parse` returns `{ ok: false }` for text
// that cannot become a valid stored value (e.g. "1.5" in an int column) — the
// cell then keeps the text, flags itself invalid and commits NOTHING, so a
// stray keystroke can never write null (or a fraction) into a whole-number
// column. All three commit NUMBERS, the same type submit.tsx sends.
type NumericKind = "int" | "decimal" | "money" | "percentage";

const NUMERIC_KINDS: Record<NumericKind, {
  step: string | ((field: FormField) => string);
  toText: (value: unknown, field: FormField) => string;
  parse: (
    text: string,
    field: FormField,
  ) => { ok: true; value: number | null } | { ok: false; reason?: string };
}> = {
  int: {
    step: "1",
    toText: (value) =>
      value === null || value === undefined || value === "" ? "" : String(value),
    parse: (text) => {
      if (text === "") return { ok: true, value: null };
      const n = Number(text);
      return Number.isInteger(n) ? { ok: true, value: n } : { ok: false };
    },
  },
  decimal: {
    step: decimalCellStep,
    toText: displayNumber,
    // Direct data-ENTRY guard (Allan's gate-review call, 2026-10-01): a value
    // with more decimal digits than the field's effective precision
    // (max(4, decimal_places)) is REJECTED outright — nothing is committed —
    // rather than silently rounded/truncated. Never applied to the Decimal
    // Places SETTING control itself (field designer), only to typing a VALUE
    // into an existing decimal field's cell. See numeric-input-step.ts.
    parse: (text, field) => {
      if (text === "") return { ok: true, value: null };
      const n = Number(text);
      if (isNaN(n)) return { ok: false };
      if (exceedsDecimalPrecision(text, field.decimal_places)) {
        const max = Math.max(DEFAULT_DECIMAL_PLACES, field.decimal_places ?? 0);
        return {
          ok: false,
          reason: `Too many decimal places (max ${max})`,
        };
      }
      return { ok: true, value: n };
    },
  },
  money: {
    // Money's step stays fixed regardless of the field's own `decimal_places`
    // (display-formatting only for money) — `decimalCellStep` already returns
    // "0.01" for any non-"decimal" field type.
    step: "0.01",
    toText: displayNumber,
    // Money is NOT in scope for the storage-widening change (plan D1): MONEY
    // always stores at a FIXED 4 decimal places in the database, so the
    // ceiling here is fixed at 4 — it never widens with the field's own
    // `decimal_places` setting the way the "decimal" kind's does. See
    // exceedsMoneyPrecision in numeric-input-step.ts.
    parse: (text) => {
      if (text === "") return { ok: true, value: null };
      const n = Number(text);
      if (isNaN(n)) return { ok: false };
      if (exceedsMoneyPrecision(text)) {
        return {
          ok: false,
          reason: "Too many decimal places (max 4)",
        };
      }
      return { ok: true, value: n };
    },
  },
  percentage: {
    step: "0.01",
    // Stored 0.29 → "29", never "28.999999999999996": round the display
    // percent to 6 places and strip trailing zeros.
    toText: (value) => {
      const pct = toDisplayPercentage(value);
      return pct === "" ? "" : String(Number(pct.toFixed(6)));
    },
    parse: (text) => {
      if (text === "") return { ok: true, value: null };
      const n = toStoredPercentage(text);
      return n === null ? { ok: false } : { ok: true, value: n };
    },
  },
};

// A numeric cell holds its OWN text so the user can type "250." or "12.5"
// without the value being re-rendered under the cursor on every keystroke.
// The row value is committed (as a number or null) on each valid change, and
// the text is re-seeded only when the row value changes underneath the cell
// (a conflict refresh), never on its own commit.
function NumericCell({
  kind,
  value,
  field,
  onCommit,
  onValidityChange,
  disabled,
}: {
  kind: NumericKind;
  value: unknown;
  field: FormField;
  onCommit: (n: number | null) => void;
  /** Reports every transition so the page can refuse to save while a cell
   *  shows text that was never committed (screen ≠ saved). */
  onValidityChange: (valid: boolean) => void;
  disabled: boolean;
}) {
  const spec = NUMERIC_KINDS[kind];
  const step = typeof spec.step === "function" ? spec.step(field) : spec.step;
  const toNumber = (v: unknown): number | null =>
    v === null || v === undefined || v === "" || isNaN(Number(v)) ? null : Number(v);
  const [text, setText] = useState(() => spec.toText(value, field));
  const [invalid, setInvalid] = useState(false);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  const lastCommitted = useRef<number | null>(toNumber(value));
  // Latest callback in a ref: it is a per-render closure over stable page
  // setters, and re-running the re-seed effect on it would re-seed every render.
  const validityRef = useRef(onValidityChange);
  validityRef.current = onValidityChange;
  useEffect(() => {
    const incoming = toNumber(value);
    if (incoming !== lastCommitted.current) {
      lastCommitted.current = incoming;
      setText(spec.toText(value, field));
      setInvalid(false);
      setInvalidReason(null);
      validityRef.current(true);
    }
  }, [value, field, spec]);
  return (
    <Input
      type="number"
      step={step}
      value={text}
      aria-invalid={invalid || undefined}
      title={
        invalid
          ? (invalidReason ?? (kind === "int" ? "Whole numbers only" : "Enter a number"))
          : undefined
      }
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        // A browser reports "" for a partial it cannot represent ("-", "1e")
        // and flags it badInput — that is not a clear, it is unfinished input.
        const parsed: { ok: true; value: number | null } | { ok: false; reason?: string } =
          e.target.validity?.badInput ? { ok: false } : spec.parse(next, field);
        if (!parsed.ok) {
          setInvalid(true);
          setInvalidReason(parsed.reason ?? null);
          onValidityChange(false);
          return; // keep the previous committed value; nothing is written
        }
        setInvalid(false);
        setInvalidReason(null);
        onValidityChange(true);
        lastCommitted.current = parsed.value;
        onCommit(parsed.value);
      }}
      disabled={disabled}
      className={cn("h-8 text-sm", invalid && "border-destructive focus-visible:ring-destructive")}
      placeholder={field.name}
    />
  );
}

export const Route = createFileRoute("/portal/myapp/$tableId/grid")({
  component: GridCapturePage,
  validateSearch: (search: Record<string, unknown>) => ({
    mode: (search.mode as string) || "create",
    ids: (search.ids as string) || "",
  }),
});

function GridCapturePage() {
  const { tableId } = Route.useParams();
  const { mode, ids } = Route.useSearch();
  const { token } = useAuth();
  // Mirror the live token in a ref so the long-lived `fetchForm` callback isn't
  // recreated when a silent JWT refresh rotates `token`. Were `token` in
  // fetchForm's deps, a refresh would recreate it, re-run the mount effect and
  // flash the loading screen mid-capture — the very thing this view is meant to
  // avoid on app switch. Same idiom as submit.tsx / HeaderLineForm (Layer 1).
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const formId = tableId;

  // Edit mode detection
  const isEditMode = mode === "edit";
  const editIds = useMemo(
    () => (isEditMode && ids ? ids.split(",").map(Number).filter((n) => !isNaN(n)) : []),
    [isEditMode, ids]
  );

  // Object-level permissions — API is single source of truth
  const { permissions: objPerms, loading: permsLoading, denied: permsDenied } = useMyAppPermissions(formId);
  const permissionDenied = permsDenied || (objPerms !== null && (isEditMode ? !objPerms.can_edit : !objPerms.can_submit));
  const canSubmit = objPerms?.can_submit ?? false;
  const canEdit = objPerms?.can_edit ?? false;

  const [form, setForm] = useState<FormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Edit mode only: tracks the per-record fetch that happens AFTER `form`
  // resolves. Without this the page renders header + empty body for a beat
  // before rows pop in. Skeleton rows render against this flag instead.
  const [recordsLoading, setRecordsLoading] = useState(false);

  // Grid state
  const [rows, setRows] = useState<GridRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  // Linked field options cache
  const [linkedOptions, setLinkedOptions] = useState<
    Record<string, LinkedOption[]>
  >({});

  // Lookup values per row (rowId -> fieldId -> value)
  const [rowLookupValues, setRowLookupValues] = useState<RowLookupValues>({});

  // Cache for linked table field definitions (keyed by linked table id)
  const [linkedTableFields, setLinkedTableFields] = useState<
    Record<string, FormField[]>
  >({});

  // Column widths for resizable headers (fieldId -> width in px)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  // Edit mode: track original values for dirty detection
  const [originalValues, setOriginalValues] = useState<Record<string, Record<string, unknown>>>({});
  // Edit mode: track which cells are dirty (rowId -> set of column names)
  const [dirtyFields, setDirtyFields] = useState<Record<string, Set<string>>>({});
  // Cells whose text was never committed (an int cell reading "4.5", a bare
  // "-"). Save is refused while any exist, so what is on screen is always
  // what is saved. rowId -> set of column names.
  const [invalidCells, setInvalidCells] = useState<Record<string, Set<string>>>({});
  const setCellValidity = useCallback((rowId: string, columnName: string, valid: boolean) => {
    setInvalidCells((prev) => {
      const had = prev[rowId]?.has(columnName) ?? false;
      if (valid === !had) return prev;
      const next = new Set(prev[rowId] ?? []);
      if (valid) next.delete(columnName);
      else next.add(columnName);
      return { ...prev, [rowId]: next };
    });
  }, []);
  const invalidCount = useMemo(
    () => Object.values(invalidCells).reduce((n, set) => n + set.size, 0),
    [invalidCells],
  );
  // Workflow state machine (per-table). Empty array when the table has no states.
  const [tableStates, setTableStates] = useState<TableState[]>([]);
  // Per-row workflow state — the record's whole `_state` block, parsed by
  // `extractRecordState` when records are loaded in edit mode.
  // `available_transitions` drives "Move selected to…" (MyApp's own per-record
  // answer about where a row may go); `current_state` drives per-cell lockdown
  // so users see locked fields greyed out, matching what the backend rejects.
  const [rowStateCtx, setRowStateCtx] = useState<Record<string, RecordStateContext | null>>({});
  const rowStates = useMemo(() => {
    const out: Record<string, CurrentStateInfo | null> = {};
    for (const [rowId, ctx] of Object.entries(rowStateCtx)) out[rowId] = ctx?.current_state ?? null;
    return out;
  }, [rowStateCtx]);

  // "Move selected to…" (bulk state move). `moveConfirm` is the one plain
  // confirmation; `moving` is the in-flight run; `moveResult` is the banner
  // that stays until the next move or save; `pendingReview` is a registered
  // pre-advance review (the Price Book's push review) opened in its place.
  const [moveConfirm, setMoveConfirm] = useState<{ target: TableState; plan: BulkMovePlan } | null>(null);
  const [moving, setMoving] = useState<{ done: number; total: number } | null>(null);
  // The re-read after a push review. The page stays locked for it exactly as
  // it does for a plain run's re-read: a keystroke landing mid-re-read would
  // be overwritten by the fresh rows, and a bin press would be undone.
  const [rereading, setRereading] = useState(false);
  const pageLocked = moving !== null || rereading;
  const [moveResult, setMoveResult] = useState<
    | { kind: "moved"; targetName: string; moved: number; refused: RefusedRow[]; notMoved: NotMovedRow[]; reread: boolean }
    | { kind: "review"; reread: boolean }
    | null
  >(null);
  const [pendingReview, setPendingReview] = useState<{
    Review: React.ComponentType<PreAdvanceReviewProps>;
    targetStateId: string;
    pressedRecordId: number;
  } | null>(null);

  // Get visible, editable fields (exclude hidden, lookup, calculation, concatenation, auto-numbered)
  // Workflow state columns (SysCurrentStateId / SysCurrentStateName) are also
  // excluded — they're governed by the transition endpoint, not direct writes.
  const editableFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter(
      (f) =>
        !f.is_hidden &&
        !f.is_auto_numbered &&
        !STATE_SYSTEM_COLUMNS.has(f.column_name) &&
        f.field_type &&
        !["lookup", "calculation", "concatenation", "rollup"].includes(f.field_type.code)
    );
  }, [form]);

  // Shared-FK collision link columns on this table (2+ links to the same
  // lookup table sharing one physical FK column). On Grid Edit — which only
  // ever edits EXISTING records — these are locked read-only and stripped from
  // the batch-update payload so a save can't overwrite each field's own good
  // snapshot with the shared last-write-wins FK. Tier 1 "stop the bleed".
  const sharedFkCollisionCols = useMemo(
    () => getSharedFkCollisionColumns(form?.fields ?? []),
    [form?.fields],
  );

  // Get calculation fields (displayed as read-only computed columns)
  const calculationFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter(
      (f) => !f.is_hidden && f.field_type?.code === "calculation"
    );
  }, [form]);

  // Get concatenation fields (displayed as read-only computed columns)
  const concatenationFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter(
      (f) => !f.is_hidden && f.field_type?.code === "concatenation"
    );
  }, [form]);

  // Get lookup fields (displayed as read-only auto-filled columns)
  const lookupFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter(
      (f) => !f.is_hidden && f.field_type?.code === "lookup"
    );
  }, [form]);

  // Get rollup fields (displayed as read-only server-computed columns)
  const rollupFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter(
      (f) => !f.is_hidden && f.field_type?.code === "rollup"
    );
  }, [form]);

  // Edit mode identity column: the table's display field (what the list page
  // and link labels call this record). Display fields are not unique, so the
  // PK is always shown beneath it — see recordIdentity.
  const displayField = useMemo(
    () => form?.fields.find((f) => f.id === form.display_field_id) ?? null,
    [form],
  );

  // Which record a Grid Edit row IS. `label` is the display-field value
  // (a link display field reads its `<col>_display` label), null when the
  // table has no display field or the value is empty; `pk` is the row's
  // primary key. Rendered sticky-left so it survives a 20-column scroll.
  const recordIdentity = useCallback(
    (row: GridRow): { label: string | null; pk: number | undefined } => {
      let label: string | null = null;
      if (displayField) {
        const col = displayField.column_name;
        const raw =
          displayField.field_type?.code === "link"
            ? (row[`${col}_display`] ?? row[col])
            : row[col];
        if (raw !== null && raw !== undefined && raw !== "") {
          label = formatCellValue(raw, displayField);
        }
      }
      return { label, pk: row._pkValue };
    },
    [displayField],
  );

  // Get column width for a field (use stored width or default)
  const getColumnWidth = useCallback(
    (fieldId: string) => columnWidths[fieldId] || 150,
    [columnWidths]
  );

  // Handle column resize
  const handleColumnResize = useCallback((fieldId: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [fieldId]: width }));
  }, []);

  // Compute a concatenation field value for a given row (including lookup values)
  const computeConcatValue = useCallback(
    (field: FormField, row: GridRow): string | null => {
      if (!form || !field.concatenation_template) return null;
      // Get lookup values for this row
      const lookups = rowLookupValues[row._rowId] || {};
      return evaluateConcatenation(
        field.concatenation_template,
        form.fields,
        row as Record<string, unknown>,
        lookups
      );
    },
    [form, rowLookupValues]
  );

  // All fields that have show_total (for footer)
  const totalFields = useMemo(() => {
    if (!form) return new Set<string>();
    return new Set(
      form.fields.filter((f) => f.show_total).map((f) => f.id)
    );
  }, [form]);

  // Compute a calculation field's DISPLAY value for a given row. v2 (T-SQL)
  // fields evaluate via evaluateIr, never evaluateFormula — that only
  // understands the legacy `+ - * /` mini-language and would mis-parse
  // T-SQL syntax as arithmetic. This is a read-only preview helper (footer
  // sums, grid cells); it must NEVER be used to decide what gets sent in a
  // save payload — see the explicit v2 omission at each payload site below.
  const computeCalcValue = useCallback(
    (field: FormField, row: GridRow): number | null => {
      if (!form) return null;
      const lookups = rowLookupValues[row._rowId] || {};
      if (field.calculation_spec_version === 2) {
        const irResult = evaluateIr(
          field.calculation_ir,
          buildFieldValueMap(form.fields, row as Record<string, unknown>, lookups),
        );
        return isComputedOnSave(irResult) ? null : irResult;
      }
      if (!field.calculation_formula) return null;
      const result = evaluateFormula(
        field.calculation_formula,
        form.fields,
        row as Record<string, unknown>,
        lookups
      );
      if (result !== null && !isNaN(Number(result))) return Number(result);
      return null;
    },
    [form, rowLookupValues]
  );

  // Fetch lookup values when a link field value changes
  const fetchLookupValues = useCallback(
    async (rowId: string, linkFieldId: string, linkedRecordId: number | null) => {
      if (!form) return;

      // Find lookup fields that depend on this link field
      const dependentLookups = lookupFields.filter(
        (f) => f.source_link_field_id === linkFieldId
      );

      if (dependentLookups.length === 0) return;

      // If no linked record selected, clear the lookup values
      if (linkedRecordId === null) {
        setRowLookupValues((prev) => {
          const rowValues = { ...(prev[rowId] || {}) };
          for (const lookup of dependentLookups) {
            delete rowValues[lookup.id];
          }
          return { ...prev, [rowId]: rowValues };
        });
        return;
      }

      // Find the link field to get linked table info
      const linkField = form.fields.find((f) => f.id === linkFieldId);
      if (!linkField?.linked_table_id) return;

      try {
        // Fetch the linked record
        const linkedRecord = await myappApi.records.getLinked(
          token,
          linkField.linked_table_id,
          linkedRecordId
        );

        if (!linkedRecord) return;

        // Get the linked table's field definitions from cache
        const linkedFields = linkedTableFields[linkField.linked_table_id] || [];

        // Extract lookup values for each dependent lookup field
        const newLookups: Record<string, unknown> = {};
        for (const lookup of dependentLookups) {
          if (lookup.lookup_field_id) {
            // Find the target field in the linked table's fields
            const targetField = linkedFields.find(
              (f) => f.id === lookup.lookup_field_id
            );
            if (targetField) {
              newLookups[lookup.id] = linkedRecord[targetField.column_name];
            }
          }
        }

        setRowLookupValues((prev) => ({
          ...prev,
          [rowId]: { ...(prev[rowId] || {}), ...newLookups },
        }));
      } catch (err) {
        console.error("Failed to fetch lookup values:", err);
      }
    },
    [form, token, lookupFields, linkedTableFields]
  );

  // Returns true on a clean load, false if it set an error (so the manual
  // Refresh button can report honestly rather than always claiming success).
  // `token` is read via `tokenRef.current` and intentionally absent from the
  // deps so a silent JWT refresh doesn't recreate this callback (see tokenRef).
  const fetchForm = useCallback(
    async (opts?: { silent?: boolean }): Promise<boolean> => {
      try {
        // Silent mode (the manual Refresh button) reloads the definition + linked
        // options in the background without the full-page "Loading table…" flash,
        // so entered rows stay visible. Cold mount uses the normal loading screen.
        if (!opts?.silent) setLoading(true);
        const formData = await myappApi.tables.get(tokenRef.current, formId);
        if (!formData.is_published) {
          setError("Table must be published before adding data");
          return false;
        }
        if (formData.needs_republish) {
          setError("The table design has been changed. Please contact your administrator to re-publish the table before adding records.");
          return false;
        }
        // Grid Entry (create mode) is manual record creation — only capture
        // tables allow it. Editing existing rows (edit mode) is left intact.
        if (!isEditMode && !canAddRecords(formData.table_type)) {
          setError(manualEntryBlockedReason(formData.table_type));
          return false;
        }
        setForm(formData);
        setError(null);

        // Pre-fetch linked options and linked table field definitions for link fields
        const linkFields = formData.fields.filter(
          (f) => f.field_type?.code === "link" && f.linked_table_id
        );
        for (const field of linkFields) {
          if (field.linked_table_id) {
            try {
              const options = await myappApi.records.getLinkedOptions(
                tokenRef.current,
                formId,
                field.linked_table_id,
                { displayFieldId: field.linked_display_field_id || undefined }
              );
              setLinkedOptions((prev) => ({
                ...prev,
                [field.id]: options,
              }));

              // Also fetch linked table field definitions for lookup resolution
              const linkedForm = await myappApi.tables.get(tokenRef.current, field.linked_table_id);
              setLinkedTableFields((prev) => ({
                ...prev,
                [field.linked_table_id!]: linkedForm.fields,
              }));
            } catch (err) {
              console.error(`Failed to load options for ${field.name}:`, err);
            }
          }
        }
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load table");
        return false;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    // `token` intentionally omitted — read via tokenRef.current (see above).

    [formId, isEditMode]
  );

  // Manual, on-demand refresh of the field definitions + linked options (the
  // toolbar Refresh button). Background/silent — keeps any entered rows visible
  // and avoids the loading-screen flash, now that the auto-refetch-on-focus is
  // gone.
  const [refreshingForm, setRefreshingForm] = useState(false);
  const handleManualRefresh = useCallback(async () => {
    setRefreshingForm(true);
    try {
      const ok = await fetchForm({ silent: true });
      if (ok) toast.success("Field definitions refreshed");
      else toast.error("Couldn't refresh field definitions");
    } finally {
      setRefreshingForm(false);
    }
  }, [fetchForm]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  // NOTE: a `visibilitychange` listener used to re-run fetchForm() every time the
  // window regained focus (to catch field edits made in another tab). It fired on
  // every app switch — e.g. tabbing to Excel and back while capturing — flipping
  // the grid into its loading state and reloading the definition + linked options,
  // so it never sat static. Removed so the grid stays put on app switch (matching
  // the submit forms + the global refetchOnWindowFocus:false). The explicit
  // "Refresh" button in the toolbar reloads field definitions on demand.

  // Workflow states are per-table and shared across all selected rows.
  // Fetch once when the form is identified. Mirrors the loader in submit.tsx.
  useEffect(() => {
    if (!token || !formId) return;
    let cancelled = false;
    myappApi.tableStates
      .list(token, formId)
      .then((sts) => {
        if (!cancelled) setTableStates(sts);
      })
      .catch(() => {
        if (!cancelled) setTableStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, formId]);

  // Edit mode: read every `editIds` record and (re)seed the grid from what is
  // stored — rows, dirty baselines, workflow state. Runs on mount and again
  // after a bulk state move (plan D9): a move changes `SysCurrentStateName`,
  // `_state.available_transitions` and which cells are locked, and stamps
  // `SysUpdatedAtUtc`, so every row is stale once it returns. Resetting the
  // dirty set is safe there because a move is refused while any cell is dirty.
  // `ids` defaults to the URL's; a re-read passes the rows still ON the grid,
  // so a row the person removed with the bin does not come back.
  const loadEditRecords = useCallback(async (ids: number[] = editIds): Promise<boolean> => {
    try {
      const fetchedRows: GridRow[] = [];
      const originals: Record<string, Record<string, unknown>> = {};
      const states: Record<string, RecordStateContext | null> = {};
      for (const id of ids) {
        const record = await myappApi.records.get(tokenRef.current, formId, id);
        if (record) {
          const rowId = `row_edit_${id}`;
          const row: GridRow = {
            _rowId: rowId,
            _status: "pending",
            _pkValue: id,
            ...record,
          };
          fetchedRows.push(row);
          // Store original values for dirty detection
          // For link fields, use the FK value (_fk column) since dropdown
          // selections are stored as FK IDs
          const orig: Record<string, unknown> = {};
          editableFields.forEach((f) => {
            if (f.field_type?.code === "link") {
              orig[f.column_name] = record[`${f.column_name}_fk`] ?? record[f.column_name] ?? null;
            } else {
              orig[f.column_name] = record[f.column_name] ?? null;
            }
          });
          originals[rowId] = orig;
          // The record's `_state` (same shape submit.tsx consumes), validated
          // so a partial `{}` doesn't pollute lockdown or the move menu.
          states[rowId] = extractRecordState(record as Record<string, unknown>);
        }
      }
      setRows(fetchedRows);
      setOriginalValues(originals);
      setRowStateCtx(states);
      setDirtyFields({});
      setInvalidCells({});
      return true;
    } catch (err) {
      console.error("Failed to load records for editing:", err);
      setError("Failed to load records for editing");
      return false;
    }
  }, [editIds, formId, editableFields]);

  // Initialize — empty rows for create mode, fetch records for edit mode
  useEffect(() => {
    if (!form || rows.length > 0) return;

    if (isEditMode && editIds.length > 0 && token) {
      setRecordsLoading(true);
      void loadEditRecords(editIds).finally(() => setRecordsLoading(false));
    } else {
      addRows(5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const createEmptyRow = (): GridRow => {
    const row: GridRow = {
      _rowId: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      _status: "pending",
    };
    editableFields.forEach((field) => {
      row[field.column_name] = null;
    });
    return row;
  };

  const addRows = (count: number) => {
    const newRows = Array.from({ length: count }, () => createEmptyRow());
    setRows((prev) => [...prev, ...newRows]);
  };

  const updateCell = (rowId: string, columnName: string, value: unknown, fieldId?: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row._rowId === rowId ? { ...row, [columnName]: value } : row
      )
    );
    setSaveResult(null);

    // Track dirty fields in edit mode
    if (isEditMode) {
      setDirtyFields((prev) => {
        const rowDirty = new Set(prev[rowId] || []);
        const orig = originalValues[rowId];
        if (orig && cellValuesEqual(orig[columnName], value)) {
          rowDirty.delete(columnName);
        } else {
          rowDirty.add(columnName);
        }
        return { ...prev, [rowId]: rowDirty };
      });
    }

    // If this is a link field, fetch lookup values for dependent fields
    if (fieldId && form) {
      const field = form.fields.find((f) => f.id === fieldId);
      if (field?.field_type?.code === "link") {
        fetchLookupValues(rowId, fieldId, value as number | null);
      }
    }
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((row) => row._rowId !== rowId));
    // A removed row takes its unsaved and invalid cells with it — otherwise
    // they keep Save and "Move selected to…" blocked on a row that is gone.
    const drop = <T,>(prev: Record<string, T>) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    };
    setInvalidCells(drop);
    setDirtyFields(drop);
  };

  const hasDataInRow = (row: GridRow): boolean => {
    return editableFields.some((field) => {
      const value = row[field.column_name];
      return value !== null && value !== "" && value !== undefined;
    });
  };

  // Total dirty records count (edit mode)
  const dirtyCount = useMemo(
    () => Object.values(dirtyFields).filter((s) => s.size > 0).length,
    [dirtyFields]
  );

  // Workflow-state lockdown summary across the loaded rows (edit mode only).
  // Drives a banner so users see WHY some cells are greyed out before they try
  // to type — much friendlier than letting the backend reject the batch.
  const lockdownSummary = useMemo(() => {
    if (!isEditMode || rows.length === 0) {
      return { fullyLockedRows: 0, partiallyLockedRows: 0 };
    }
    let fully = 0;
    let partial = 0;
    for (const row of rows) {
      const currentState = rowStates[row._rowId];
      if (!currentState) continue;
      if (currentState.is_terminal) {
        fully += 1;
        continue;
      }
      const ctx = buildStateLockdownContext(tableStates, currentState);
      const anyLocked = editableFields.some((f) => isFieldLockedByState(f, ctx));
      if (anyLocked) partial += 1;
    }
    return { fullyLockedRows: fully, partiallyLockedRows: partial };
  }, [isEditMode, rows, rowStates, tableStates, editableFields]);

  const handleSave = async () => {
    if (invalidCount > 0) {
      setError(`${invalidCount} cell${invalidCount === 1 ? " has" : "s have"} invalid input — fix the red cells before saving`);
      return;
    }
    if (isEditMode) {
      // Edit mode: batch update only dirty records
      const recordsToUpdate: Record<string, unknown>[] = [];
      const dirtyRowIds: string[] = [];

      // Build the base payload: only dirty + unlocked columns, keyed by PK.
      // Pure helper so the dirty-tracking + lockdown filtering combination
      // can be unit-tested without rendering the grid. Locked rows surface
      // in `skippedRowIds` for downstream visibility.
      const { records: baseRecords, skippedRowIds: _skipped } = buildBatchUpdatePayload({
        rows,
        dirtyFields,
        editableFields,
        primaryKeyColumn: form!.primary_key_column,
        states: tableStates,
        rowStates,
        collisionColumns: sharedFkCollisionCols,
      });

      // Layer computed-field values (calc, concat, lookup) onto each base
      // record. These aren't user-edited cells but the backend recomputes
      // them on update, so sending the FE-computed value keeps the UI in
      // sync with the saved row immediately.
      for (const record of baseRecords) {
        const pkValue = record[form!.primary_key_column];
        const row = rows.find((r) => r._pkValue === pkValue);
        if (!row) continue;
        calculationFields.forEach((field) => {
          // v2 (T-SQL) calc columns are OMITTED entirely, never sent — SQL
          // Server is the only thing that ever computes a v2 stored value,
          // inside the same statement that writes the row.
          if (field.calculation_spec_version === 2) return;
          const calcVal = computeCalcValue(field, row);
          if (calcVal !== null) record[field.column_name] = calcVal;
        });
        concatenationFields.forEach((field) => {
          const concatVal = computeConcatValue(field, row);
          if (concatVal !== null) record[field.column_name] = concatVal;
        });
        lookupFields.forEach((field) => {
          const lookupVal = rowLookupValues[row._rowId]?.[field.id];
          if (lookupVal !== null && lookupVal !== undefined) record[field.column_name] = lookupVal;
        });
        // Per-row optimistic-concurrency token (plan D21). The row was
        // read with this value; if someone else has saved since, the
        // backend refuses THAT row rather than silently overwriting them.
        // Per row, not per save: a grid holds rows read at different times.
        const rowVersion = row[VERSION_TOKEN_KEY];
        if (typeof rowVersion === "string" && rowVersion) {
          record[VERSION_TOKEN_KEY] = rowVersion;
        }
        recordsToUpdate.push(record);
        dirtyRowIds.push(row._rowId);
      }

      if (recordsToUpdate.length === 0) {
        setError("No changes to save");
        return;
      }

      setSaving(true);
      setSaveResult(null);
      setMoveResult(null);

      try {
        const result = await myappApi.records.updateBatch(token, formId, recordsToUpdate);

        // Rows somebody else saved first. They are NOT failures the user
        // can fix by retrying blindly: the row underneath them has moved,
        // so refresh it in place, keep what they typed, and let them
        // decide. No merge UI — see RecordConflictToast.
        const conflicts: RecordChangedDetail[] = result.failed
          .filter(isRecordChangedFailure)
          .map((f) => {
            const current = f.current ?? null;
            return {
              recordId: f.id,
              current,
              actorEmail:
                typeof current?.SysUserEmail === "string"
                  ? current.SysUserEmail
                  : undefined,
              changedAtUtc:
                typeof current?.[VERSION_TOKEN_KEY] === "string"
                  ? (current[VERSION_TOKEN_KEY] as string)
                  : undefined,
            };
          });
        const conflictByPk = new Map(
          conflicts.map((c) => [c.recordId, c] as const),
        );

        setRows((prev) =>
          prev.map((row) => {
            if (!dirtyRowIds.includes(row._rowId)) return row;
            const failedItem = result.failed.find(
              (f) => f.id === row._pkValue
            );
            if (failedItem) {
              const conflict = conflictByPk.get(row._pkValue as number);
              if (!conflict?.current) {
                return { ...row, _status: "error" as const, _error: failedItem.error };
              }
              // Refresh every column the user is NOT currently editing,
              // so the row shows what is actually stored — including the
              // new version token, without which a second save would
              // conflict again on a value the user has already seen.
              const dirty = dirtyFields[row._rowId] ?? new Set<string>();
              const refreshed: Record<string, unknown> = { ...row };
              for (const [col, value] of Object.entries(conflict.current)) {
                // A link column arrives as three keys — `Supplier`,
                // `Supplier_fk`, `Supplier_display` — while the dirty set
                // holds only the field's own `column_name`. Matching on
                // the raw key alone would leave `Supplier` untouched and
                // still overwrite `Supplier_fk`, which is the one the
                // cell is BOUND to: the user's in-progress selection
                // would be silently replaced by the stored one.
                const base = col.replace(/_(fk|display)$/, "");
                if (dirty.has(col) || dirty.has(base)) continue;
                refreshed[col] = value;
              }
              return {
                ...(refreshed as typeof row),
                _status: "error" as const,
                _error: failedItem.error,
              };
            }
            return { ...row, _status: "success" as const };
          })
        );

        // Re-baseline the conflicted rows against what is now stored, so
        // the dirty set keeps meaning "differs from the server" rather
        // than "differs from what the server said before someone else
        // saved".
        if (conflicts.length > 0) {
          setOriginalValues((prev) => {
            const next = { ...prev };
            for (const row of rows) {
              const conflict = conflictByPk.get(row._pkValue as number);
              if (!conflict?.current) continue;
              const orig: Record<string, unknown> = { ...(next[row._rowId] ?? {}) };
              editableFields.forEach((f) => {
                // Link fields are dirty-tracked by FK id, exactly as the
                // initial load seeds them (see the `_fk` fallback where
                // `originalValues` is first built). Re-baselining a link
                // column to `current[col]` — the display snapshot —
                // would leave every link cell permanently "dirty"
                // against a value it can never equal.
                const fkKey = `${f.column_name}_fk`;
                if (f.field_type?.code === "link" && fkKey in conflict.current!) {
                  orig[f.column_name] = conflict.current![fkKey] ?? null;
                } else if (f.column_name in conflict.current!) {
                  orig[f.column_name] = conflict.current![f.column_name] ?? null;
                }
              });
              next[row._rowId] = orig;
            }
            return next;
          });
          showBatchConflictToast(conflicts);
        }

        // Clear dirty tracking for updated rows
        setDirtyFields((prev) => {
          const next = { ...prev };
          for (const rowId of dirtyRowIds) {
            if (!result.failed.some((f) => f.id === rows.find((r) => r._rowId === rowId)?._pkValue)) {
              delete next[rowId];
            }
          }
          return next;
        });

        // Update original values for successfully updated rows
        setOriginalValues((prev) => {
          const next = { ...prev };
          for (const row of rows) {
            if (dirtyRowIds.includes(row._rowId) && !result.failed.some((f) => f.id === row._pkValue)) {
              const orig: Record<string, unknown> = {};
              editableFields.forEach((f) => {
                orig[f.column_name] = row[f.column_name] ?? null;
              });
              next[row._rowId] = orig;
            }
          }
          return next;
        });

        setSaveResult({
          succeeded: result.updated_count,
          failed: result.failed_count,
        });

        toast.success(`Updated ${result.updated_count} record${result.updated_count !== 1 ? "s" : ""}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update records");
      } finally {
        setSaving(false);
      }
      return;
    }

    // Create mode: batch create new records
    const rowsWithData = rows.filter(hasDataInRow);
    if (rowsWithData.length === 0) {
      setError("No data to save");
      return;
    }

    setSaving(true);
    setSaveResult(null);

    const records = rowsWithData.map((row) => {
      const record: Record<string, unknown> = {};
      editableFields.forEach((field) => {
        // Shared-FK collision: never write a collided link column on create.
        // The grid cell stores the picked FK id in the [col] snapshot slot, so
        // sending it would stamp an id through the legacy shared FK and make a
        // brand-new record look "old" (shared FK populated). The cell is locked
        // in renderCell; this is the matching write-path guard. Forward capture
        // (per-field SysFk<Col>) will reopen these. See shared-fk-collision.ts.
        if (isSharedFkCollisionField(field, sharedFkCollisionCols)) return;
        const value = row[field.column_name];
        if (value !== null && value !== "" && value !== undefined) {
          record[field.column_name] = value;
        }
      });
      calculationFields.forEach((field) => {
        // v2 (T-SQL) calc columns are OMITTED entirely, never sent — SQL
        // Server is the only thing that ever computes a v2 stored value,
        // inside the same statement that writes the row.
        if (field.calculation_spec_version === 2) return;
        const calcVal = computeCalcValue(field, row);
        if (calcVal !== null) record[field.column_name] = calcVal;
      });
      concatenationFields.forEach((field) => {
        const concatVal = computeConcatValue(field, row);
        if (concatVal !== null) record[field.column_name] = concatVal;
      });
      lookupFields.forEach((field) => {
        const lookupVal = rowLookupValues[row._rowId]?.[field.id];
        if (lookupVal !== null && lookupVal !== undefined) record[field.column_name] = lookupVal;
      });
      return record;
    });

    try {
      const result = await myappApi.records.createBatch(token, formId, records);

      setRows((prev) =>
        prev.map((row) => {
          const rowIndex = rowsWithData.findIndex((r) => r._rowId === row._rowId);
          if (rowIndex === -1) return row;

          const created = result.created.find(
            (c) => (c as { _row_index?: number })._row_index === rowIndex
          );
          const failed = result.failed.find(
            (f) => f._row_index === rowIndex
          );

          if (created) {
            return { ...row, _status: "success" as const };
          } else if (failed) {
            return { ...row, _status: "error" as const, _error: failed._error };
          }
          return row;
        })
      );

      setSaveResult({
        succeeded: result.created_count,
        failed: result.failed_count,
      });

      // Remove successful rows and add new empty rows
      setTimeout(() => {
        setRows((prev) => {
          const remainingRows = prev.filter((row) => row._status !== "success");
          if (remainingRows.length < 5) {
            const newRows = Array.from(
              { length: 5 - remainingRows.length },
              () => createEmptyRow()
            );
            return [...remainingRows, ...newRows];
          }
          return remainingRows;
        });
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save records");
    } finally {
      setSaving(false);
    }
  };

  // Per-cell workflow-state lockdown lookup. Used by renderCell (to disable
  // inputs), the TableCell wrapper (to grey + tooltip the cell), and handleSave
  // (to exclude locked dirty fields from the update payload as defence-in-depth
  // alongside the backend's `_check_state_lockdown`).
  const getCellLock = useCallback(
    (row: GridRow, field: Pick<FormField, "locked_at_state_id">): { locked: boolean; title?: string } => {
      if (!isEditMode) return { locked: false };
      const currentState = rowStates[row._rowId] ?? null;
      if (!currentState) return { locked: false };
      const lockCtx = buildStateLockdownContext(tableStates, currentState);
      if (!isFieldLockedByState(field, lockCtx)) return { locked: false };
      const reason = lockReasonForField(field, lockCtx);
      return {
        locked: true,
        title: `Locked — record is in '${currentState.name}'${reason ? ` (field is read-only after '${reason}')` : ""}`,
      };
    },
    [isEditMode, tableStates, rowStates],
  );

  // ── "Move selected to…" (bulk state move) ─────────────────────────────
  // Every grid row, named the way the sticky "Record" column names it, with
  // the `_state` MyApp returned for it.
  const bulkRows = useMemo<BulkMoveRow[]>(() => {
    if (!isEditMode || !form) return [];
    return rows
      .filter((row) => typeof row._pkValue === "number")
      .map((row) => {
        const { label, pk } = recordIdentity(row);
        const pkText = `${form.primary_key_column} ${pk}`;
        return {
          rowId: row._rowId,
          recordId: row._pkValue as number,
          label: label !== null ? `${label} · ${pkText}` : pkText,
          state: rowStateCtx[row._rowId] ?? null,
        };
      });
  }, [isEditMode, form, rows, recordIdentity, rowStateCtx]);

  // The table's states in workflow order, each with how many rows MyApp
  // would let move there now and the review (if any) that stands in front
  // of the move. Reviews resolve by NAME — ids do not survive a rebuild.
  const moveMenu = useMemo(
    () =>
      [...tableStates]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((state) => ({
          state,
          movable: countMovable(bulkRows, state.id),
          Review: resolvePreAdvanceReview(form?.organisation_id, formId, state.name),
        })),
    [tableStates, bulkRows, form?.organisation_id, formId],
  );

  // Shown only where the record form would offer a move too: a stated table
  // with at least one row MyApp says may go somewhere (plan D16).
  const showMoveControl =
    isEditMode && canEdit && moveMenu.some((item) => item.movable > 0);
  const moveBlockedByEdits = dirtyCount > 0 || invalidCount > 0;

  const chooseMoveTarget = (target: TableState, Review: React.ComponentType<PreAdvanceReviewProps> | null) => {
    const plan = planBulkStateMove(bulkRows, target);
    if (Review) {
      // 🔴 THE REVIEW IS THE CONFIRMATION, AND IT GETS ONE ROW. Exactly what
      // pressing "Advance" on this row's record form would open: no list, no
      // selection — the review derives its own set server-side
      // (preAdvanceReview.ts). The grid's other rows are neither passed nor
      // excluded.
      if (plan.pressedRecordId === null) return;
      setMoveResult(null);
      setPendingReview({ Review, targetStateId: target.id, pressedRecordId: plan.pressedRecordId });
      return;
    }
    setMoveConfirm({ target, plan });
  };

  const runMove = async (target: TableState, plan: BulkMovePlan) => {
    setMoveConfirm(null);
    // The re-read below discards dirty and invalid cells on the strength of
    // this rule; enforce it here too, not only on the trigger's `disabled`.
    if (moveBlockedByEdits || pageLocked) return;
    setSaveResult(null);
    setMoveResult(null);
    setMoving({ done: 0, total: plan.send.length });
    try {
      const { moved, refused } = await runBulkStateMove(
        plan.send,
        (recordId) => myappApi.tableStates.transition(tokenRef.current, formId, recordId, target.id),
        (done) => setMoving({ done, total: plan.send.length }),
      );
      // Whatever happened, every row is re-read: states, transitions and
      // locked cells are stale after any move (plan D9).
      const reread = await loadEditRecords(bulkRows.map((r) => r.recordId));
      setMoveResult({
        kind: "moved",
        targetName: target.name,
        moved: moved.length,
        refused,
        notMoved: plan.notMoved,
        reread,
      });
    } finally {
      setMoving(null);
    }
  };

  // A run is hundreds of independent transactions, and nothing cancels the
  // loop: leaving mid-run keeps it writing with nobody left to read the
  // result. So while the page is locked, in-app navigation (sidebar links,
  // browser Back) is refused and closing the tab asks first.
  const pageLockedRef = useRef(pageLocked);
  pageLockedRef.current = pageLocked;
  useBlocker({
    shouldBlockFn: () => {
      if (!pageLockedRef.current) return false;
      toast.error("Wait for the move to finish — the remaining records are still being moved.");
      return true;
    },
    enableBeforeUnload: () => pageLockedRef.current,
  });

  const renderCell = (row: GridRow, field: FormField) => {
    const value = row[field.column_name];
    const { locked: stateLocked } = getCellLock(row, field);
    const isDisabled =
      row._status === "saving" || row._status === "success" || stateLocked || pageLocked;

    if (!field.field_type) return null;

    // Shared-FK collision link cell: render the field's own stored snapshot
    // read-only on EVERY row — existing AND new. On an existing row the editable
    // combobox would bind to the shared, unreliable FK and a save would write it
    // back over the good snapshot. On a NEW grid row it is worse: the grid cell
    // model stores a link edit as the picked FK *id* in the [col] (snapshot)
    // slot — capturing through the legacy shared FK and stamping a value into a
    // record that should be identifiably new (shared FK NULL). Until forward
    // capture writes the per-field SysFk<Col>, collided link cells stay locked
    // in the grid for new and existing rows alike. Tier 1 "stop the bleed".
    // NB: old-vs-new is the SHARED FK (value ⇒ old), never SysFk — see
    // shared-fk-collision.ts / fk_naming.py CRITICAL INVARIANT.
    if (isSharedFkCollisionField(field, sharedFkCollisionCols)) {
      const snapshot = row[field.column_name];
      return (
        <span
          className="block truncate text-xs"
          title="Locked for review"
        >
          {snapshot !== null && snapshot !== undefined && snapshot !== ""
            ? formatCellValue(snapshot, field)
            : <span className="text-muted-foreground italic">&mdash;</span>}
        </span>
      );
    }

    // Workflow-state-locked cell: the value can never be edited on this row,
    // so render it the way the list page does (formatCellValue) instead of a
    // disabled input — "No" for a false bit, "249.00" for money, the link's
    // display label — so a locked row still reads as a record, not as blanks.
    if (stateLocked) {
      const display =
        field.field_type.code === "link"
          ? (row[`${field.column_name}_display`] as string | null | undefined) ??
            formatCellValue(value, field)
          : formatCellValue(value, field);
      return (
        <span className="block truncate px-2 text-sm text-muted-foreground" title={display}>
          {display}
        </span>
      );
    }

    // The switch handles the REAL FieldType codes (bit / money / decimal / int /
    // bigint / percentage …). Anything unknown falls to the free-text default.
    switch (field.field_type.code) {
      case "text":
      case "longtext":
      case "email":
      case "guid":
        return (
          <Input
            value={(value as string) || ""}
            onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
            placeholder={field.name}
          />
        );

      case "int":
      case "bigint":
      case "decimal":
      case "money":
      case "percentage":
        return (
          <NumericCell
            kind={
              field.field_type.code === "int" || field.field_type.code === "bigint"
                ? "int"
                : field.field_type.code === "percentage"
                  ? "percentage"
                  : field.field_type.code === "money"
                    ? "money"
                    : "decimal"
            }
            value={value}
            field={field}
            onCommit={(n) => updateCell(row._rowId, field.column_name, n)}
            onValidityChange={(valid) => setCellValidity(row._rowId, field.column_name, valid)}
            disabled={isDisabled}
          />
        );

      case "date":
        return (
          <Input
            type="date"
            value={(value as string) || ""}
            onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
          />
        );

      case "time":
        return (
          <Input
            type="time"
            value={(value as string) || ""}
            onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
          />
        );

      case "datetime":
        return (
          <Input
            type="datetime-local"
            value={(value as string) || ""}
            onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
          />
        );

      case "bit":
        return (
          <label className="flex items-center gap-2 px-2 h-8 text-sm cursor-pointer">
            <Checkbox
              checked={value === true}
              aria-label={field.name}
              onCheckedChange={(checked) =>
                updateCell(row._rowId, field.column_name, checked === true)
              }
              disabled={isDisabled}
            />
            <span className="text-muted-foreground">{value === true ? "Yes" : "No"}</span>
          </label>
        );

      case "link": {
        const options = (linkedOptions[field.id] || []).map((opt) => ({
          value: String(opt.id),
          label: opt.display_value || String(opt.id),
        }));
        // Use _fk column for the dropdown value (FK ID), falling back to the
        // raw column value for backwards compatibility with in-memory edits
        const fkValue = row[`${field.column_name}_fk`] ?? value;
        // getLinkedOptions is capped at 500 rows server-side. When the row's
        // current FK is beyond that cap the Combobox has no label for it and
        // would show the placeholder, so seed the row's own `<col>_display`
        // label as an option — the cell then reads as the record it is.
        const currentDisplay = row[`${field.column_name}_display`];
        if (
          fkValue !== null &&
          fkValue !== undefined &&
          typeof currentDisplay === "string" &&
          currentDisplay !== "" &&
          !options.some((o) => o.value === String(fkValue))
        ) {
          options.unshift({ value: String(fkValue), label: currentDisplay });
        }
        return (
          <Combobox
            options={options}
            value={fkValue !== null && fkValue !== undefined ? String(fkValue) : ""}
            onValueChange={(v) =>
              updateCell(row._rowId, field.column_name, v ? Number(v) : null, field.id)
            }
            placeholder={`Select ${field.name}`}
            searchPlaceholder={`Search ${field.name.toLowerCase()}...`}
            emptyMessage="No options found."
            allowClear={!field.is_required}
            disabled={isDisabled}
            className="h-8 text-sm"
            ariaLabel={field.name}
          />
        );
      }

      case "options": {
        const optionsConfig = parseOptionsConfig(field.options_config);
        if (!optionsConfig) {
          // Misconfigured options field (no/invalid config) — degrade to free
          // text, matching the shared field-renderer's fallback.
          return (
            <Input
              value={(value as string) || ""}
              onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
              disabled={isDisabled}
              className="h-8 text-sm"
              placeholder={field.name}
            />
          );
        }
        return (
          <OptionsFieldInput
            config={optionsConfig}
            value={value}
            onChange={(v) => updateCell(row._rowId, field.column_name, v)}
            fieldName={field.name}
            required={field.is_required}
            disabled={isDisabled}
            className="h-8 text-sm"
            compact
          />
        );
      }

      case "signature":
        return value && Number(value) > 0
          ? <span className="text-xs font-medium">Signed</span>
          : <span className="text-xs text-muted-foreground italic">&mdash;</span>;

      case "attachment": {
        const count = Number(value ?? 0);
        return count > 0
          ? <span className="text-xs font-medium">{count} file{count === 1 ? "" : "s"}</span>
          : <span className="text-xs text-muted-foreground italic">&mdash;</span>;
      }

      default:
        return (
          <Input
            value={(value as string) || ""}
            onChange={(e) => updateCell(row._rowId, field.column_name, e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
            placeholder={field.name}
          />
        );
    }
  };

  if (loading || permsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading table...</div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message={isEditMode
          ? "You don't have permission to edit records in this table."
          : "You don't have permission to submit records to this table."}
        backTo={`/portal/myapp/${tableId}`}
        backLabel="Back to Table"
      />
    );
  }

  if (error && !form) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-destructive">{error}</div>
        <Link to="/portal/myapp/tables" search={{}}>
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tables
          </Button>
        </Link>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* While a move is running the way out is removed, not just greyed:
              a disabled button inside a link does not stop the link. */}
          {pageLocked ? (
            <Button variant="ghost" size="icon" disabled aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          ) : (
            <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }}>
              <Button variant="ghost" size="icon" aria-label="Back">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
          )}
          <div>
            <h1 className="text-2xl font-bold">
              {form.name} - {isEditMode ? "Edit Records" : "Grid Entry"}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode
                ? `Editing ${editIds.length} record${editIds.length !== 1 ? "s" : ""}${dirtyCount > 0 ? ` • ${dirtyCount} changed` : ""}`
                : "Enter multiple records at once using the grid below"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleManualRefresh}
            disabled={refreshingForm || pageLocked}
            title="Reload field definitions and linked-field options without losing entered rows"
          >
            <RefreshCw
              className={cn("h-4 w-4 mr-2", refreshingForm && "animate-spin")}
            />
            Refresh
          </Button>
          {!isEditMode && canSubmit && (
            <Button variant="outline" onClick={() => addRows(5)}>
              <Plus className="h-4 w-4 mr-2" />
              Add 5 Rows
            </Button>
          )}
          {showMoveControl && (
            // A disabled button swallows hover, so the reason sits on a wrapper.
            <span
              title={
                moveBlockedByEdits
                  ? "Save your changes first"
                  : form.is_locked
                    ? "Table is locked"
                    : undefined
              }
            >
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="bulk-move-trigger"
                    disabled={moveBlockedByEdits || saving || pageLocked || form.is_locked}
                  >
                    {moving
                      ? `Moving ${moving.done} of ${moving.total}…`
                      : rereading
                        ? "Re-reading rows…"
                        : "Move selected to…"}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-w-sm">
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Rows that can move / rows on this grid
                  </DropdownMenuLabel>
                  {moveMenu.map(({ state, movable, Review }) => (
                    <DropdownMenuItem
                      key={state.id}
                      disabled={movable === 0}
                      onSelect={() => chooseMoveTarget(state, Review)}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <span className="flex w-full items-center justify-between gap-4">
                        <span>{state.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {movable} of {bulkRows.length}
                        </span>
                      </span>
                      {Review && (
                        <span className="text-xs text-muted-foreground">
                          (opens the push review — reviews every edited row, not only these)
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          )}
          {(isEditMode ? canEdit : canSubmit) && (
            <Button
              onClick={handleSave}
              disabled={saving || pageLocked || invalidCount > 0 || (isEditMode && dirtyCount === 0) || form?.is_locked}
            >
              <Save className="h-4 w-4 mr-2" />
              {form?.is_locked
                ? "Table Locked"
                : saving
                  ? "Saving..."
                  : invalidCount > 0
                    ? `Fix ${invalidCount} invalid cell${invalidCount !== 1 ? "s" : ""}`
                    : isEditMode
                      ? `Save ${dirtyCount} Change${dirtyCount !== 1 ? "s" : ""}`
                      : "Save All"}
            </Button>
          )}
        </div>
      </div>

      {/* Workflow-state lockdown banner — surfaces locked rows before edit */}
      {isEditMode && (lockdownSummary.fullyLockedRows > 0 || lockdownSummary.partiallyLockedRows > 0) && (
        <Card className="mb-4 border-amber-500">
          <CardContent className="py-3 text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span>
              {lockdownSummary.fullyLockedRows > 0 && (
                <>
                  <strong>{lockdownSummary.fullyLockedRows}</strong> row
                  {lockdownSummary.fullyLockedRows === 1 ? " is" : "s are"} in a
                  terminal workflow state and fully read-only.{" "}
                </>
              )}
              {lockdownSummary.partiallyLockedRows > 0 && (
                <>
                  <strong>{lockdownSummary.partiallyLockedRows}</strong> row
                  {lockdownSummary.partiallyLockedRows === 1 ? " has" : "s have"}{" "}
                  some fields locked by their current state. Locked cells are
                  greyed out and skipped on save.
                </>
              )}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Shared-FK collision notice — collided link columns are locked on
          edit (see sharedFkCollisionCols). */}
      {isEditMode && sharedFkCollisionCols.size > 0 && (
        <Card className="mb-4">
          <CardContent className="py-3">
            <SharedFkCollisionNotice />
          </CardContent>
        </Card>
      )}

      {/* Save result banner */}
      {saveResult && (
        <Card className={`mb-4 ${saveResult.failed > 0 ? "border-amber-500" : "border-green-500"}`}>
          <CardContent className="py-3">
            <div className="flex items-center gap-4">
              {saveResult.failed === 0 ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <span>
                <strong>{saveResult.succeeded}</strong> record(s){" "}
                {isEditMode ? "updated" : "created"} successfully
                {saveResult.failed > 0 && (
                  <>, <strong className="text-amber-600">{saveResult.failed}</strong> failed</>
                )}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk state move result — one line per row that did not move */}
      {moveResult?.kind === "review" && (
        <Card
          className={`mb-4 ${moveResult.reread ? "border-green-500" : "border-amber-500"}`}
          data-testid="bulk-move-result"
        >
          <CardContent className="py-3 flex items-center gap-4">
            {moveResult.reread ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            <span>
              {moveResult.reread
                ? "Push review completed — rows re-read"
                : "Push review completed — the rows could not be re-read; reload the page before editing"}
            </span>
          </CardContent>
        </Card>
      )}
      {moveResult?.kind === "moved" && (
        <Card
          data-testid="bulk-move-result"
          className={`mb-4 ${moveResult.refused.length + moveResult.notMoved.length > 0 ? "border-amber-500" : "border-green-500"}`}
        >
          <CardContent className="py-3 space-y-2">
            <div className="flex items-center gap-4">
              {moveResult.refused.length + moveResult.notMoved.length === 0 ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <span>
                <strong>{moveResult.moved}</strong> moved to &apos;{moveResult.targetName}&apos;,{" "}
                <strong className={moveResult.refused.length > 0 ? "text-amber-600" : undefined}>
                  {moveResult.refused.length}
                </strong>{" "}
                refused, <strong>{moveResult.notMoved.length}</strong> not moved
                {!moveResult.reread &&
                  " — the rows could not be re-read; reload the page before editing"}
              </span>
            </div>
            {moveResult.refused.length + moveResult.notMoved.length > 0 && (
              <ul className="ml-9 space-y-1 text-sm">
                {moveResult.refused.map(({ row, reason }) => (
                  <li key={`refused-${row.rowId}`}>
                    <span className="font-medium">{row.label}</span>
                    <span className="text-muted-foreground"> — refused: </span>
                    {reason}
                  </li>
                ))}
                {moveResult.notMoved.map(({ row, reason }) => (
                  <li key={`not-moved-${row.rowId}`}>
                    <span className="font-medium">{row.label}</span>
                    <span className="text-muted-foreground"> — not moved: </span>
                    {reason}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error banner */}
      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="py-3">
            <div className="flex items-center gap-4 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid */}
      <Card>
        <CardContent className="p-0">
          <ScrollableTable maxHeight="calc(100vh - 280px)">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cn("w-10", isEditMode && "sticky left-0 z-20 bg-background")}>#</TableHead>
                  {isEditMode && (
                    <TableHead className="sticky left-10 z-20 bg-background min-w-[160px] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                      Record
                    </TableHead>
                  )}
                  {editableFields.map((field) => (
                    <ResizableHeader
                      key={field.id}
                      fieldId={field.id}
                      width={getColumnWidth(field.id)}
                      onResize={handleColumnResize}
                    >
                      <div className="flex items-center gap-1">
                        {field.original_name ? (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="truncate cursor-default">
                                  {field.name}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p className="text-xs text-muted-foreground">Original heading</p>
                                <p>{field.original_name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          field.name
                        )}
                        {field.is_required && (
                          <span className="text-destructive">*</span>
                        )}
                      </div>
                    </ResizableHeader>
                  ))}
                  {calculationFields.map((field) => (
                    <TableHead key={field.id} className="min-w-[120px]">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {field.name}
                        <span className="text-xs italic">(calc)</span>
                      </div>
                    </TableHead>
                  ))}
                  {concatenationFields.map((field) => (
                    <TableHead key={field.id} className="min-w-[120px]">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {field.name}
                        <span className="text-xs italic">(auto)</span>
                      </div>
                    </TableHead>
                  ))}
                  {lookupFields.map((field) => (
                    <TableHead key={field.id} className="min-w-[120px]">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {field.name}
                        <span className="text-xs italic">(lookup)</span>
                      </div>
                    </TableHead>
                  ))}
                  {rollupFields.map((field) => (
                    <TableHead key={field.id} className="min-w-[120px]">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {field.name}
                        <span className="text-xs italic">(rollup)</span>
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="w-16 sticky right-10 bg-background z-10 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                    Status
                  </TableHead>
                  <TableHead className="w-10 sticky right-0 bg-background z-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Skeleton rows for the brief gap between form-loaded and
                    records-loaded in edit mode. Without this the user sees
                    header + empty body for a beat after "Loading table..."
                    clears — visually janky. Match the number of skeletons
                    to editIds so the layout doesn't reflow when rows land. */}
                {isEditMode && recordsLoading && rows.length === 0 && (
                  Array.from({ length: editIds.length }, (_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell className="text-muted-foreground text-sm sticky left-0 z-10 bg-background">{i + 1}</TableCell>
                      <TableCell className="sticky left-10 z-10 bg-background p-1">
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                      {editableFields.map((field) => (
                        <TableCell key={field.id} className="p-1">
                          <Skeleton className="h-8 w-full" />
                        </TableCell>
                      ))}
                      {calculationFields.map((field) => (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                      ))}
                      {concatenationFields.map((field) => (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      ))}
                      {lookupFields.map((field) => (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                      ))}
                      {rollupFields.map((field) => (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                      ))}
                      <TableCell className="sticky right-10 z-10 bg-background" />
                      <TableCell className="sticky right-0 z-10 bg-background" />
                    </TableRow>
                  ))
                )}
                {rows.map((row, index) => (
                  <TableRow
                    key={row._rowId}
                    className={
                      row._status === "success"
                        ? "bg-green-50"
                        : row._status === "error"
                        ? "bg-red-50"
                        : ""
                    }
                  >
                    <TableCell
                      className={cn(
                        "text-muted-foreground text-sm",
                        isEditMode && "sticky left-0 z-10",
                        isEditMode && rowStickyBg(row),
                      )}
                    >
                      {index + 1}
                    </TableCell>
                    {isEditMode && (() => {
                      const { label, pk } = recordIdentity(row);
                      return (
                        <TableCell
                          data-testid="record-identity"
                          className={cn(
                            "sticky left-10 z-10 min-w-[160px] max-w-[240px] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]",
                            rowStickyBg(row),
                          )}
                        >
                          <div className="flex flex-col leading-tight">
                            {label !== null && (
                              <span className="text-sm font-medium truncate" title={label}>
                                {label}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {form.primary_key_column} {pk}
                            </span>
                          </div>
                        </TableCell>
                      );
                    })()}
                    {editableFields.map((field) => {
                      const isDirty = isEditMode && dirtyFields[row._rowId]?.has(field.column_name);
                      const { locked: stateLocked, title: lockTitle } = getCellLock(row, field);
                      return (
                        <TableCell
                          key={field.id}
                          className={cn(
                            "p-1",
                            isDirty && "bg-amber-50",
                            stateLocked && "bg-muted/40",
                          )}
                          title={lockTitle}
                        >
                          {renderCell(row, field)}
                        </TableCell>
                      );
                    })}
                    {calculationFields.map((field) => {
                      const calcVal = computeCalcValue(field, row);
                      const displayText = calcVal !== null ? formatNumber(calcVal, field.decimal_places ?? undefined) : "—";
                      return (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <span
                            className="text-sm font-mono text-muted-foreground px-2 block truncate"
                            title={displayText}
                          >
                            {displayText}
                          </span>
                        </TableCell>
                      );
                    })}
                    {concatenationFields.map((field) => {
                      const concatVal = computeConcatValue(field, row);
                      const displayText = concatVal || "—";
                      return (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <span
                            className="text-sm text-muted-foreground px-2 block truncate"
                            title={displayText}
                          >
                            {displayText}
                          </span>
                        </TableCell>
                      );
                    })}
                    {lookupFields.map((field) => {
                      const lookupVal = rowLookupValues[row._rowId]?.[field.id];
                      const displayVal = lookupVal !== null && lookupVal !== undefined
                        ? typeof lookupVal === "number"
                          ? formatNumber(lookupVal, field.decimal_places ?? undefined)
                          : String(lookupVal)
                        : "—";
                      return (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <span
                            className="text-sm text-muted-foreground px-2 block truncate"
                            title={displayVal}
                          >
                            {displayVal}
                          </span>
                        </TableCell>
                      );
                    })}
                    {rollupFields.map((field) => {
                      const rollupVal = row[field.column_name];
                      const displayVal = rollupVal !== null && rollupVal !== undefined
                        ? formatNumber(rollupVal as number, field.decimal_places ?? undefined)
                        : "—";
                      return (
                        <TableCell key={field.id} className="p-1 max-w-[200px]">
                          <span
                            className="text-sm font-medium text-muted-foreground px-2 block truncate"
                            title={displayVal}
                          >
                            {displayVal}
                          </span>
                        </TableCell>
                      );
                    })}
                    <TableCell
                      className={cn(
                        "sticky right-10 z-10 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]",
                        row._status === "success" && "bg-green-50",
                        row._status === "error" && "bg-red-50",
                        row._status !== "success" && row._status !== "error" && "bg-background"
                      )}
                    >
                      {row._status === "success" && (
                        <Badge variant="info" className="gap-1">
                          <Check className="h-3 w-3" />
                          Saved
                        </Badge>
                      )}
                      {row._status === "error" && (
                        <Badge variant="destructive" className="gap-1" title={row._error}>
                          <X className="h-3 w-3" />
                          Error
                        </Badge>
                      )}
                      {row._status === "saving" && (
                        <Badge variant="secondary">Saving...</Badge>
                      )}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "sticky right-0 z-10",
                        row._status === "success" && "bg-green-50",
                        row._status === "error" && "bg-red-50",
                        row._status !== "success" && row._status !== "error" && "bg-background"
                      )}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => removeRow(row._rowId)}
                        disabled={row._status === "saving" || pageLocked}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {/* Footer totals for show_total fields */}
              {totalFields.size > 0 && rows.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell className="text-xs font-medium">Total</TableCell>
                    {isEditMode && <TableCell />}
                    {editableFields.map((field) => {
                      if (!totalFields.has(field.id)) {
                        return <TableCell key={field.id} />;
                      }
                      let sum = 0;
                      for (const row of rows) {
                        const num = Number(row[field.column_name]);
                        if (!isNaN(num)) sum += num;
                      }
                      return (
                        <TableCell key={field.id} className="p-1 text-sm font-bold">
                          {formatNumber(sum, field.decimal_places ?? undefined)}
                        </TableCell>
                      );
                    })}
                    {calculationFields.map((field) => {
                      if (!totalFields.has(field.id)) {
                        return <TableCell key={field.id} />;
                      }
                      let sum = 0;
                      for (const row of rows) {
                        const calcVal = computeCalcValue(field, row);
                        if (calcVal !== null) sum += calcVal;
                      }
                      return (
                        <TableCell key={field.id} className="p-1 text-sm font-bold">
                          {formatNumber(sum, field.decimal_places ?? undefined)}
                        </TableCell>
                      );
                    })}
                    {concatenationFields.map((field) => (
                      <TableCell key={field.id} />
                    ))}
                    {lookupFields.map((field) => (
                      <TableCell key={field.id} />
                    ))}
                    {rollupFields.map((field) => {
                      if (!totalFields.has(field.id)) {
                        return <TableCell key={field.id} />;
                      }
                      let sum = 0;
                      for (const row of rows) {
                        const num = Number(row[field.column_name]);
                        if (!isNaN(num)) sum += num;
                      }
                      return (
                        <TableCell key={field.id} className="p-1 text-sm font-bold">
                          {formatNumber(sum, field.decimal_places ?? undefined)}
                        </TableCell>
                      );
                    })}
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </ScrollableTable>
        </CardContent>
      </Card>

      {/* The one confirmation for a plain bulk move (plan D6). */}
      <AlertDialog open={moveConfirm !== null} onOpenChange={(open) => !open && setMoveConfirm(null)}>
        {moveConfirm && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Move {moveConfirm.plan.send.length} of {bulkRows.length} record
                {bulkRows.length === 1 ? "" : "s"} to &apos;{moveConfirm.target.name}&apos;?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Each record is moved on its own, and every server check still runs for each one.
                Each move is recorded, and anything subscribed to this table&apos;s state changes
                runs once per record.
                {moveConfirm.plan.notMoved.length > 0 &&
                  ` ${moveConfirm.plan.notMoved.length} record${moveConfirm.plan.notMoved.length === 1 ? "" : "s"} will not be moved:`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {moveConfirm.plan.notMoved.length > 0 && (
              <ul className="max-h-48 overflow-y-auto space-y-1 text-sm" data-testid="bulk-move-not-moved">
                {moveConfirm.plan.notMoved.map(({ row, reason }) => (
                  <li key={row.rowId}>
                    <span className="font-medium">{row.label}</span>
                    <span className="text-muted-foreground"> — {reason}</span>
                  </li>
                ))}
              </ul>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => void runMove(moveConfirm.target, moveConfirm.plan)}
              >
                Move {moveConfirm.plan.send.length} record{moveConfirm.plan.send.length === 1 ? "" : "s"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>

      {/* A registered pre-advance review (the Price Book's push review), opened
          exactly as the record form opens it: the table's OWNING org, the
          target id, and ONE pressed row — the first row MyApp says may move,
          i.e. the row whose form would show this advance (plan D11/D12). */}
      {pendingReview && form.organisation_id && (
        <pendingReview.Review
          tableId={formId}
          organisationId={form.organisation_id}
          pressedRecordId={pendingReview.pressedRecordId}
          targetStateId={pendingReview.targetStateId}
          onDone={() => {
            setPendingReview(null);
            // The review has toasted its own counts; re-read every row.
            setRereading(true);
            void loadEditRecords(bulkRows.map((r) => r.recordId))
              .then((reread) => setMoveResult({ kind: "review", reread }))
              .finally(() => setRereading(false));
          }}
          onCancel={() => setPendingReview(null)}
        />
      )}

      {/* Instructions */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {isEditMode ? (
            <ul className="list-disc list-inside space-y-1">
              <li>Each row is an existing record — the Record column shows which one</li>
              <li>Only changed cells are saved; click &quot;Save N Changes&quot; when you are done</li>
              <li>Cells greyed out are locked by the record&apos;s workflow state</li>
              <li>Failed rows show an error badge — hover over it for details</li>
            </ul>
          ) : (
            <ul className="list-disc list-inside space-y-1">
              <li>Fill in multiple rows, then click &quot;Save All&quot; to create all records at once</li>
              <li>Required fields are marked with <span className="text-destructive">*</span></li>
              <li>Successfully saved rows will be removed and replaced with empty rows</li>
              <li>Failed rows will show an error status - hover over the badge to see details</li>
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
